// Reading the per-policy device results (/api/policy-results): what each policy reaches, and what
// each device reports for it. The backend does the fetching; this only reshapes it for the screens.

export const STATUS_LABEL = {
  compliant: 'Opfyldt',
  nonCompliant: 'Ikke opfyldt',
  error: 'Fejl',
  conflict: 'Konflikt',
  pending: 'Afventer',
  notApplicable: 'Ikke relevant',
};

const PROBLEMS = ['nonCompliant', 'error', 'conflict'];
export const isProblem = (status) => PROBLEMS.includes(status);

export const policyKey = (category, id) => `${category}:${id}`;

// The numbers for one policy: how many devices it reaches and what they report.
export function entrySummary(entry) {
  if (!entry) return null;
  const problems = entry.nonCompliant + entry.error + entry.conflict;
  return { total: entry.total, ok: entry.compliant, problems, pending: entry.pending, nonCompliant: entry.nonCompliant, error: entry.error, conflict: entry.conflict };
}

// "82/90" on the policy list, with the tone the number should have.
export function listValue(entry) {
  const s = entrySummary(entry);
  if (!s || s.total === 0) return null;
  return { text: `${s.ok}/${s.total}`, tone: s.problems > 0 ? 'bad' : 'ok' };
}

// Everything the results say about one device: how many policies reach it, how many are in order,
// and which ones fail. `names` maps "category:id" to the policy's name and category label.
export function deviceOverview(results, names, device) {
  if (!results || !device) return null;
  const oks = [];
  const pendings = [];
  const problems = [];
  Object.entries(results.policies || {}).forEach(([key, entry]) => {
    const row = (entry.devices || []).find((d) => (device.id && d.id === device.id) || (!d.id && d.name && d.name === device.name));
    if (!row || row.status === 'notApplicable') return;
    const info = { key, status: row.status, name: names[key]?.name || 'Politik', category: names[key]?.category || '' };
    if (row.status === 'compliant') oks.push(info);
    else if (row.status === 'pending') pendings.push(info);
    else if (isProblem(row.status)) problems.push({ key, status: row.status, name: names[key]?.name || 'Politik', category: names[key]?.category || '' });
  });
  // The device's own list of policy states is the source of truth for what fails on it: a policy that the results could not
  // be matched to (two policies with one name, a result not read yet) is still shown, never silently left out.
  const failingNames = new Set();
  (device.states || []).forEach((s) => {
    if (!['nonCompliant', 'error', 'conflict'].includes(s.state) || !s.displayName) return;
    failingNames.add(s.displayName);
    if (problems.some((p) => p.name === s.displayName)) return;
    problems.push({ key: `device:${s.displayName}`, status: s.state, name: s.displayName, category: s.source === 'configuration' ? 'Konfigurationsprofil' : 'Compliance-politik' });
  });
  const okLeft = oks.filter((p) => !failingNames.has(p.name));
  return { total: okLeft.length + pendings.length + problems.length, ok: okLeft.length, pending: pendings.length, problems, oks: okLeft, pendings };
}

// "category:id" -> { name, category label } for every policy in the (public) inventory.
export function policyNames(inventory) {
  const out = {};
  Object.entries(inventory?.categories || {}).forEach(([ck, c]) => (c.items || []).forEach((it) => { out[policyKey(ck, it.id)] = { name: it.name, category: c.label }; }));
  return out;
}
